// UserRefundManagement.jsx
import React, { useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faChevronRight,
    faChevronLeft,
    faPlus,
    faSearch,
    faFilter,
    faArrowsRotate,
    faEye,
} from "@fortawesome/free-solid-svg-icons";
import {
    Row,
    Col,
    Card,
    Table,
    InputGroup,
    Form,
    Button,
    Modal,
    Badge,
} from "@themesberg/react-bootstrap";
import {
    userCreateRefundRequest,
    userGetMyRefundRequests,
    getRefundById,
} from "../../../pages/api";

const ACTIVE_BUTTON_STYLE = {
    background: "#1D4E89",
    border: "none",
    borderRadius: "0.5rem",
    color: "#ffffff",
};

const statusBadgeClass = (status) => {
    switch (String(status || "").toLowerCase()) {
        case "pending":
            return "bg-warning text-white border border-warning-subtle";
        case "processed":
        case "approved":
            return "bg-success text-white border border-success-subtle";
        case "failed":
            return "bg-danger text-white border border-danger-subtle";
        case "rejected":
            return "bg-secondary text-white border border-secondary-subtle";
        default:
            return "bg-light text-dark border border-light";
    }
};

const getPageItems = (currentPage, totalPages) => {
    if (totalPages <= 5) return Array.from({ length: totalPages }, (_, i) => i + 1);
    if (currentPage <= 2) return [1, 2, 3, "...", totalPages];
    if (currentPage >= totalPages - 1) return [1, "...", totalPages - 2, totalPages - 1, totalPages];
    return [1, "...", currentPage - 1, currentPage, currentPage + 1, "...", totalPages];
};

const getShowingRange = (pagination) => {
    const total = Number(pagination?.total || 0);
    const perPage = Number(pagination?.per_page || 10);
    const current = Math.max(1, Number(pagination?.current_page || 1));

    if (total === 0) return { start: 0, end: 0 };

    const start = (current - 1) * perPage + 1;
    const end = Math.min(current * perPage, total);
    return { start, end };
};

const formatCurrency = (value) =>
    `₹${Number(value || 0).toLocaleString("en-IN", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    })}`;

const formatDate = (value) => {
    if (!value) return "N/A";
    const date = new Date(String(value).includes(" ") ? String(value).replace(" ", "T") : value);
    if (Number.isNaN(date.getTime())) return value;
    return new Intl.DateTimeFormat("en-IN", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
    }).format(date);
};

const mapRefundItem = (item) => ({
    id: item?.id ?? Date.now(),
    refund_id: item?.refund_id || "N/A",
    transaction_id: item?.transaction_id ?? "N/A",
    refund_amount: item?.refund_amount ?? 0,
    refund_status: String(item?.status || item?.refund_status || "pending").toLowerCase(),
    gateway_refund_reference: item?.gateway_refund_reference || "N/A",
    remarks: item?.remarks || "N/A",
    created_at: item?.created_at || "N/A",
});

const emptyRefund = {
    id: null,
    refund_id: "",
    transaction_id: "",
    refund_amount: "",
    refund_status: "",
    gateway_refund_reference: "",
    remarks: "",
    created_at: "",
};

export default function UserRefundManagement() {
    const [searchTransactionId, setSearchTransactionId] = useState("");
    const [statusFilter, setStatusFilter] = useState("");
    const [refunds, setRefunds] = useState([]);
    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [detailLoading, setDetailLoading] = useState(false);

    const [pagination, setPagination] = useState({
        current_page: 1,
        last_page: 1,
        per_page: 10,
        total: 0,
    });

    const [showAddModal, setShowAddModal] = useState(false);
    const [showViewModal, setShowViewModal] = useState(false);
    const [selectedRefund, setSelectedRefund] = useState(emptyRefund);

    const [formData, setFormData] = useState({
        transaction_id: "",
        refund_amount: "",
        remarks: "",
    });

    const [errors, setErrors] = useState({});
    const searchDebounceRef = useRef(null);
    const initialLoadRef = useRef(true);
    const skipNextEffectRef = useRef(false);

    const clearSearchDebounce = () => {
        if (searchDebounceRef.current) {
            clearTimeout(searchDebounceRef.current);
            searchDebounceRef.current = null;
        }
    };

    const fetchRefunds = async (page = 1, overrides = {}) => {
        try {
            setLoading(true);

            const params = {
                page,
                per_page: overrides.per_page ?? pagination.per_page ?? 10,
            };

            const trimmedTransaction = String((overrides.transaction_id ?? searchTransactionId) || "").trim();
            const trimmedStatus = String((overrides.status ?? statusFilter) || "").trim().toLowerCase();

            if (trimmedTransaction) params.search = trimmedTransaction;
            if (trimmedStatus) params.status = trimmedStatus;

            const response = await userGetMyRefundRequests(params);
            const responseData = response?.data || {};
            const apiData = responseData?.data || {};
            const apiRefunds = Array.isArray(apiData?.refunds_requests) ? apiData.refunds_requests : [];
            const apiPagination = apiData?.pagination || {};

            setRefunds(apiRefunds.map(mapRefundItem));
            setPagination({
                current_page: Number(apiPagination.current_page || page),
                last_page: Number(apiPagination.last_page || 1),
                per_page: Number(apiPagination.per_page || params.per_page || 10),
                total: Number(apiPagination.total || 0),
            });
        } catch (error) {
            console.error("Failed to fetch refunds:", error);
            toast.error(error?.response?.data?.message || "Unable to load refund history.");
            setRefunds([]);
            setPagination({
                current_page: 1,
                last_page: 1,
                per_page: 10,
                total: 0,
            });
        } finally {
            setLoading(false);
        }
    };

    const fetchRefundById = async (id) => {
        setDetailLoading(true);
        try {
            const response = await getRefundById(id);
            const data = response?.data?.data || emptyRefund;

            setSelectedRefund({
                id: data?.id ?? id,
                refund_id: data?.refund_id || "N/A",
                transaction_id: data?.transaction_id || "N/A",
                refund_amount: data?.refund_amount || 0,
                refund_status: String(data?.status || data?.refund_status || "pending").toLowerCase(),
                gateway_refund_reference: data?.gateway_refund_reference || "N/A",
                remarks: data?.remarks || "N/A",
                created_at: data?.created_at || "N/A",
            });

            return data;
        } catch (error) {
            console.error("Failed to fetch refund details:", error);
            toast.error(error?.response?.data?.message || "Unable to load refund details.");
            return null;
        } finally {
            setDetailLoading(false);
        }
    };

    useEffect(() => {
        if (initialLoadRef.current) {
            initialLoadRef.current = false;
            fetchRefunds(1, {
                transaction_id: "",
                status: "",
            });
            return;
        }

        if (skipNextEffectRef.current) {
            skipNextEffectRef.current = false;
            return;
        }

        clearSearchDebounce();
        searchDebounceRef.current = setTimeout(() => {
            fetchRefunds(1, {
                transaction_id: searchTransactionId,
                status: statusFilter,
            });
        }, 300);

        return () => clearSearchDebounce();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchTransactionId, statusFilter]);

    const handleClearFilters = () => {
        skipNextEffectRef.current = true;
        setSearchTransactionId("");
        setStatusFilter("");
        clearSearchDebounce();

        fetchRefunds(1, {
            transaction_id: "",
            status: "",
        });
    };

    const openAddModal = () => {
        setFormData({
            transaction_id: "",
            refund_amount: "",
            remarks: "",
        });
        setErrors({});
        setShowAddModal(true);
    };

    const closeAddModal = () => {
        setShowAddModal(false);
        setErrors({});
        setFormData({
            transaction_id: "",
            refund_amount: "",
            remarks: "",
        });
    };

    const openViewModal = async (refund) => {
        const data = refund?.id ? await fetchRefundById(refund.id) : refund;
        if (data) setShowViewModal(true);
    };

    const handleChange = (e) => {
        const { name, value } = e.target;

        if (name === "refund_amount" && value !== "" && !/^\d*\.?\d*$/.test(value)) {
            return;
        }

        setFormData((prev) => ({ ...prev, [name]: value }));
        setErrors((prev) => ({ ...prev, [name]: "" }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        const trimmedTransaction = String(formData.transaction_id || "").trim();
        const trimmedRemarks = String(formData.remarks || "").trim();
        const trimmedAmount = String(formData.refund_amount || "").trim();

        const newErrors = {
            transaction_id: !trimmedTransaction ? "Transaction ID is required" : "",
            refund_amount: !trimmedAmount ? "Refund amount is required" : "",
        };

        setErrors(newErrors);

        if (newErrors.transaction_id || newErrors.refund_amount) {
            toast.error("Please fill all required fields.");
            return;
        }

        try {
            setSubmitting(true);

            const payload = {
                transaction_id: trimmedTransaction,
                refund_amount: trimmedAmount,
            };

            if (trimmedRemarks) {
                payload.remarks = trimmedRemarks;
            }

            await userCreateRefundRequest(payload);

            toast.success("Refund request submitted successfully.");
            closeAddModal();
            await fetchRefunds(pagination.current_page, {
                transaction_id: searchTransactionId,
                status: statusFilter,
            });
        } catch (error) {
            console.error("Refund save failed:", error);
            toast.error(error?.response?.data?.message || "Unable to submit refund request.");
        } finally {
            setSubmitting(false);
        }
    };

    const { start, end } = getShowingRange(pagination);
    const pageItems = getPageItems(pagination.current_page, pagination.last_page);

    return (
        <>
            <div className="d-flex justify-content-between align-items-center flex-wrap gap-3 py-4">
                <div>
                    <h2 className="h4 mb-1">Refund Requests</h2>
                </div>

                <Button type="button" style={ACTIVE_BUTTON_STYLE} className="fw-semibold px-3 ms-auto" onClick={openAddModal}>
                    <FontAwesomeIcon icon={faPlus} className="me-2" />
                    Raise Refund
                </Button>
            </div>

            <Card className="border-0 shadow-sm rounded-4 mb-4">
                <Card.Body className="p-3 p-md-4">
                    <Row className="g-3">
                        <Col xs={12} md={6}>
                            <Form.Label className="fw-semibold">Status</Form.Label>
                            <Form.Select
                                value={statusFilter}
                                onChange={(e) => setStatusFilter(e.target.value)}
                                style={{ boxShadow: "none" }}
                            >
                                <option value="">All Status</option>
                                <option value="pending">Pending</option>
                                <option value="processed">Processed</option>
                                <option value="failed">Failed</option>
                                <option value="rejected">Rejected</option>
                            </Form.Select>
                        </Col>

                        <Col xs={12} md={6}>
                            <Form.Label className="fw-semibold">Search</Form.Label>
                            <InputGroup>
                                <InputGroup.Text className="bg-white">
                                    <FontAwesomeIcon icon={faSearch} className="text-muted" />
                                </InputGroup.Text>
                                <Form.Control
                                    type="text"
                                    placeholder="Search"
                                    value={searchTransactionId}
                                    onChange={(e) => setSearchTransactionId(e.target.value)}
                                    style={{ boxShadow: "none" }}
                                />
                            </InputGroup>
                        </Col>
                    </Row>
                </Card.Body>
            </Card>

            <Card className="border-0 shadow-sm rounded-4">
                <Card.Body className="p-3 p-md-4">
                    <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
                        <div>
                            <h5 className="mb-1">My Refund Requests</h5>
                        </div>
                    </div>

                    <div className="table-responsive">
                        <Table hover className="align-middle mb-0">
                            <thead>
                                <tr>
                                    <th>Refund ID</th>
                                    <th>Transaction ID</th>
                                    <th>Refund Amount</th>
                                    <th>Refund Status</th>
                                    {/* <th>Created At</th> */}
                                    <th className="text-center">Action</th>
                                </tr>
                            </thead>
                            <tbody>
                                {loading ? (
                                    <tr>
                                        <td colSpan={6} className="text-center text-muted py-5">
                                            Loading refund requests...
                                        </td>
                                    </tr>
                                ) : refunds.length > 0 ? (
                                    refunds.map((item) => (
                                        <tr key={item.id}>
                                            <td className="fw-semibold">{item.refund_id || "N/A"}</td>
                                            <td>{item.transaction_id || "N/A"}</td>
                                            <td>{formatCurrency(item.refund_amount)}</td>
                                            <td>
                                                <Badge className={`rounded-pill px-3 py-2 text-capitalize ${statusBadgeClass(item.refund_status)}`}>
                                                    {item.refund_status || "N/A"}
                                                </Badge>
                                            </td>
                                            {/* <td>{formatDate(item.created_at)}</td> */}
                                            <td className="text-center">
                                                <Button
                                                    type="button"
                                                    variant="outline-primary"
                                                    size="sm"
                                                    onClick={() => openViewModal(item)}
                                                >
                                                    <FontAwesomeIcon icon={faEye} />
                                                    {/* View */}
                                                </Button>
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan={6} className="text-center text-muted py-5">
                                            No refund requests found.
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </Table>
                    </div>

                    <div className="d-flex justify-content-between align-items-center mt-3 flex-wrap gap-2">
                        <div className="text-muted small">
                            Showing {start} to {end} of {pagination.total} entries
                        </div>

                        <div className="d-flex align-items-center gap-2 flex-wrap">
                            <Button
                                type="button"
                                variant="outline-primary"
                                disabled={pagination.current_page <= 1 || loading}
                                onClick={() => {
                                    clearSearchDebounce();
                                    fetchRefunds(Math.max(1, pagination.current_page - 1), {
                                        transaction_id: searchTransactionId,
                                        status: statusFilter,
                                    });
                                }}
                                className="course-page-btn"
                            >
                                <FontAwesomeIcon icon={faChevronLeft} />
                            </Button>

                            {pageItems.map((item, index) =>
                                item === "..." ? (
                                    <span key={`dots-${index}`} className="text-muted px-1">
                                        ...
                                    </span>
                                ) : (
                                    <Button
                                        key={item}
                                        type="button"
                                        variant="outline-primary"
                                        disabled={loading}
                                        onClick={() => {
                                            clearSearchDebounce();
                                            fetchRefunds(item, {
                                                transaction_id: searchTransactionId,
                                                status: statusFilter,
                                            });
                                        }}
                                        className={`course-page-btn ${item === pagination.current_page ? "course-page-btn-active" : ""}`}
                                    >
                                        {item}
                                    </Button>
                                )
                            )}

                            <Button
                                type="button"
                                variant="outline-primary"
                                disabled={pagination.current_page >= pagination.last_page || loading}
                                onClick={() => {
                                    clearSearchDebounce();
                                    fetchRefunds(Math.min(pagination.last_page, pagination.current_page + 1), {
                                        transaction_id: searchTransactionId,
                                        status: statusFilter,
                                    });
                                }}
                                className="course-page-btn"
                            >
                                <FontAwesomeIcon icon={faChevronRight} />
                            </Button>
                        </div>
                    </div>
                </Card.Body>
            </Card>

            <Modal show={showAddModal} onHide={closeAddModal} centered size="lg">
                <Form onSubmit={handleSubmit}>
                    <Modal.Header closeButton>
                        <Modal.Title>Raise Refund Request</Modal.Title>
                    </Modal.Header>

                    <Modal.Body>
                        <Row className="g-3">
                            <Col xs={12} md={6}>
                                <Form.Label className="fw-semibold">
                                    Transaction ID <span className="text-danger">*</span>
                                </Form.Label>
                                <Form.Control
                                    name="transaction_id"
                                    type="text"
                                    placeholder="Enter Transaction ID"
                                    value={formData.transaction_id}
                                    onChange={handleChange}
                                    isInvalid={!!errors.transaction_id}
                                    style={{ boxShadow: "none" }}
                                />
                                <Form.Control.Feedback type="invalid">{errors.transaction_id}</Form.Control.Feedback>
                            </Col>

                            <Col xs={12} md={6}>
                                <Form.Label className="fw-semibold">
                                    Refund Amount <span className="text-danger">*</span>
                                </Form.Label>
                                <Form.Control
                                    name="refund_amount"
                                    type="text"
                                    inputMode="decimal"
                                    placeholder="Enter refund amount"
                                    value={formData.refund_amount}
                                    onChange={handleChange}
                                    isInvalid={!!errors.refund_amount}
                                    style={{ boxShadow: "none" }}
                                />
                                <Form.Control.Feedback type="invalid">{errors.refund_amount}</Form.Control.Feedback>
                            </Col>

                            <Col xs={12}>
                                <Form.Label className="fw-semibold">Remarks</Form.Label>
                                <Form.Control
                                    as="textarea"
                                    rows={5}
                                    name="remarks"
                                    placeholder="Optional remarks"
                                    value={formData.remarks}
                                    onChange={handleChange}
                                    style={{ boxShadow: "none" }}
                                />
                            </Col>
                        </Row>
                    </Modal.Body>

                    <Modal.Footer>
                        <Button onClick={closeAddModal} disabled={submitting}>
                            Cancel
                        </Button>
                        <Button type="submit" style={ACTIVE_BUTTON_STYLE} disabled={submitting}>
                            {submitting ? "Submitting..." : "Submit Request"}
                        </Button>
                    </Modal.Footer>
                </Form>
            </Modal>

            <Modal show={showViewModal} onHide={() => setShowViewModal(false)} centered size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>Refund Details</Modal.Title>
                </Modal.Header>

                <Modal.Body>
                    {detailLoading ? (
                        <div className="text-center text-muted py-4">Loading refund details...</div>
                    ) : (
                        <Row className="g-3">
                            <Col xs={12} md={6}>
                                <Card className="border-1 shadow-sm rounded-4 h-100">
                                    <Card.Body>
                                        <div className="text-muted small">Refund ID</div>
                                        <div className="fw-semibold">{selectedRefund.refund_id}</div>
                                    </Card.Body>
                                </Card>
                            </Col>

                            <Col xs={12} md={6}>
                                <Card className="border-1 shadow-sm rounded-4 h-100">
                                    <Card.Body>
                                        <div className="text-muted small">Transaction ID</div>
                                        <div className="fw-semibold">{selectedRefund.transaction_id}</div>
                                    </Card.Body>
                                </Card>
                            </Col>

                            <Col xs={12} md={6}>
                                <Card className="border-1 shadow-sm rounded-4 h-100">
                                    <Card.Body>
                                        <div className="text-muted small">Refund Amount</div>
                                        <div className="fw-semibold">{formatCurrency(selectedRefund.refund_amount)}</div>
                                    </Card.Body>
                                </Card>
                            </Col>

                            <Col xs={12} md={6}>
                                <Card className="border-1 shadow-sm rounded-4 h-100">
                                    <Card.Body>
                                        <div className="text-muted small">Refund Status</div>
                                        <div>
                                            <Badge className={`rounded-pill px-3 py-2 text-capitalize ${statusBadgeClass(selectedRefund.refund_status)}`}>
                                                {selectedRefund.refund_status}
                                            </Badge>
                                        </div>
                                    </Card.Body>
                                </Card>
                            </Col>

                            <Col xs={12} md={6}>
                                <Card className="border-1 shadow-sm rounded-4 h-100">
                                    <Card.Body>
                                        <div className="text-muted small">Gateway Refund Reference</div>
                                        <div>{selectedRefund.gateway_refund_reference}</div>
                                    </Card.Body>
                                </Card>
                            </Col>

                            <Col xs={12} md={6}>
                                <Card className="border-1 shadow-sm rounded-4 h-100">
                                    <Card.Body>
                                        <div className="text-muted small">Created At</div>
                                        <div>{formatDate(selectedRefund.created_at)}</div>
                                    </Card.Body>
                                </Card>
                            </Col>

                            <Col xs={12}>
                                <Card className="border-1 shadow-sm rounded-4 h-100">
                                    <Card.Body>
                                        <div className="text-muted small">Remarks</div>
                                        <div>{selectedRefund.remarks}</div>
                                    </Card.Body>
                                </Card>
                            </Col>
                        </Row>
                    )}
                </Modal.Body>

                {/* <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowViewModal(false)}>
            Close
          </Button>
        </Modal.Footer> */}
            </Modal>
        </>
    );
}